import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, CircleCheckBig, Clapperboard, Copy, Download, FileText, LoaderCircle, Play, Scissors, Upload } from 'lucide-react';
import { useT } from '@/i18n';
import { ChipGroup, Empty, ErrorBox, Ltr, Modal, QueryView, Segmented, Spinner, StatusPill } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { isSessionBusy, useDrilldown, usePresence, useWs, useWsMutation } from '@/lib/api/hooks';
import { CreateTaskButton, PresenceBar, PresenceConflict } from '@/components/tasks';
import { ALL_FORMATS, APPROVE_ALL_SCORE, HOOK_STYLES, PACES, RENDER_FORMATS, SOCIAL_MOODS, STAGES, can } from '@/lib/constants';
import { clipDuration, mmss, timecode } from '@/lib/format';
import { downloadPack, downloadRender, safeName, TIMELINE_EXTRAS } from '@/lib/download';
import { logError, logInfo } from '@/lib/log';
import { useMembership } from '@/store/session';
import type { DrillNode, Drilldown, PipelineSession, Presence, RenderFormat, Tier } from '@/lib/api/types';
import { EpisodeHeader, useEpisodeContext } from './context';
import { DeviceRouting } from './CloudFallback';
import { FixEnhancePanel, hasAnalysis } from './FixEnhancePanel';
// The cut editor loads when someone opens it (WS W: it now carries the Live Studio handoff, kept out of the entry chunk).
const CutEditor = lazy(() => import('./CutEditor').then((m) => ({ default: m.CutEditor })));

// ── Stage progress ───────────────────────────────────────────────
export function StageBar({ session }: { session: PipelineSession }) {
  const { t, td } = useT();
  const qc = useQueryClient();
  const w = useWs();
  const membership = useMembership();
  const byStage = new Map(session.stages.map((s) => [s.stage, s]));
  const failed = session.stages.find((s) => s.status === 'failed');
  const needsClap = session.sources.filter((src) => src.sync?.needs_manual_clap);
  const busy = session.status === 'queued' || session.status === 'processing';
  const overall = typeof session.progress === 'number' ? Math.round(session.progress * 100) : null;
  const retry = useWsMutation(async () => {
    logInfo('pipeline', `retry processing session=${session.id} (force)`);
    await api.pipeline.process(session.id, true);
    await qc.invalidateQueries({ queryKey: qk.session(w, session.id) });
  });
  return (
    <section className="card" aria-label={t('processing')} style={{ padding: '10px 14px', display: 'grid', gap: 10 }}>
      <ol className="row wrap" style={{ gap: 6, listStyle: 'none', margin: 0, padding: 0 }}>
        {STAGES.filter((stage) => byStage.has(stage)).map((stage) => {
          const s = byStage.get(stage);
          const status = s?.status ?? 'queued';
          const tone = status === 'succeeded' ? ['var(--green-soft)', 'var(--green)'] : status === 'failed' ? ['var(--red-soft)', 'var(--accent-text)'] : status === 'running' ? ['var(--blue-soft)', 'var(--blue-text)'] : ['var(--raise)', 'var(--muted)'];
          const label = status === 'succeeded' ? t('stageDone') : status === 'failed' ? t('stageFailed') : status === 'running' ? t('stageRunning') : status === 'cancelled' ? t('stageCancelled') : t('stageQueued');
          return (
            <li key={stage} className="row" title={s?.message ?? label} style={{ gap: 6, height: 26, padding: '0 10px', borderRadius: 99, background: tone[0], color: tone[1], fontSize: 12, fontWeight: 500 }}>
              {status === 'succeeded' ? <Check size={12} aria-hidden /> : status === 'failed' ? <CircleAlert size={12} aria-hidden /> : status === 'running' ? <LoaderCircle size={12} className="spin" aria-hidden /> : null}
              {td(`stage_${stage}`)}
              {status === 'running' ? <Ltr className="mono">{Math.round((s?.progress ?? 0) * 100)}%</Ltr> : null}
              <span className="sr-only">{label}</span>
            </li>
          );
        })}
        <li className="muted small push">{t('costSoFar', { cost: session.cost_usd.toFixed(2) })}</li>
      </ol>
      {busy && overall !== null ? (
        <div className="progress" role="progressbar" aria-label={t('overallProgress')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={overall}>
          <span style={{ width: `${overall}%` }} />
        </div>
      ) : null}
      {session.stages.filter((s) => s.status === 'running').map((s) => (
        <div key={s.stage} className="stack" style={{ gap: 4 }}>
          <div className="progress" role="progressbar" aria-label={td(`stage_${s.stage}`)} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(s.progress * 100)}>
            <span style={{ width: `${Math.round(s.progress * 100)}%` }} />
          </div>
          {s.message ? <span className="muted small">{s.message}</span> : null}
        </div>
      ))}
      {needsClap.length ? (
        <div role="status" className="alert alert-warn small">
          <strong>{t('syncClap')}</strong>{' '}
          {needsClap.map((src) => t('syncConfidence', { source: src.label || src.filename, pct: Math.round((src.sync?.confidence ?? 0) * 100) })).join(' · ')}
        </div>
      ) : null}
      {session.status === 'failed' || failed ? (
        <div className="alert alert-error small row wrap" role="alert">
          <span style={{ flex: 1 }}>{t('sessionFailed', { error: session.error ?? failed?.error ?? td(`stage_${failed?.stage ?? 'ingest'}`) })}</span>
          {can(membership?.role, 'videos.edit') ? (
            <button type="button" className="btn btn-sm" disabled={retry.isPending} onClick={() => retry.mutate(undefined)}>{t('retryProcessing')}</button>
          ) : null}
        </div>
      ) : session.error ? (
        <div className="alert alert-warn small">{session.error}</div>
      ) : null}
      {retry.error ? <ErrorBox error={retry.error} /> : null}
    </section>
  );
}

// ── Player ───────────────────────────────────────────────────────
function PlayerModal({ node, sessionId, onClose, presence }: { node: DrillNode; sessionId: string | null; onClose: () => void; presence: Presence | null }) {
  const { t } = useT();
  const membership = useMembership();
  const [cutting, setCutting] = useState(false);
  const [format, setFormat] = useState<RenderFormat>(node.format);
  const [error, setError] = useState<unknown>(null);
  const [mediaError, setMediaError] = useState(false);
  const rerender = useWsMutation(() => api.pipeline.render(node.id, format), { onSuccess: () => logInfo('pipeline', `re-render edit=${node.id} format=${format}`) });
  const qc = useQueryClient();
  const w = useWs();
  const allFormats = useWsMutation(() => api.pipeline.formats(node.variant_of ?? node.id, [...ALL_FORMATS]), {
    onSuccess: (res) => {
      logInfo('pipeline', `all formats edit=${node.variant_of ?? node.id} created=${res.created.length} existing=${res.existing.length}`);
      void qc.invalidateQueries({ queryKey: ['ws', w, 'drilldown'] });
    },
  });
  // While a re-render runs, keep the previous finished video playable.
  const r = node.render?.status === 'ready' ? node.render : (node.last_ready_render ?? node.render);
  const ready = r?.status === 'ready' && !!r.url;
  const rerendering = !!node.render && (node.render.status === 'queued' || node.render.status === 'rendering');
  const aspect = node.format.replace(':', '/');
  const dl = async (kind: 'mp4' | 'srt') => {
    setError(null);
    try {
      await downloadRender(r!.id, kind, `${safeName(node.title)}.${kind}`);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <Modal title={node.title} onClose={onClose} wide>
      {can(membership?.role, 'videos.edit') ? <PresenceConflict presence={presence} editingEditId={node.id} videoTitle={node.title} /> : null}
      <div className="media" style={{ aspectRatio: aspect, maxHeight: '60vh', margin: '0 auto', width: node.format === '9:16' ? 'min(100%, 320px)' : '100%' }}>
        {ready && !mediaError ? (
          <video src={r!.url!} poster={r!.poster_url ?? undefined} controls autoPlay style={{ width: '100%', height: '100%' }} onError={() => { logError('pipeline', `cannot play render=${r!.id}`); setMediaError(true); }} />
        ) : (
          <div className="stripes" style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 13 }}>
            {mediaError ? t('cantPlay') : t('renderPending')}
          </div>
        )}
      </div>
      {rerendering ? <span className="muted small row"><LoaderCircle size={12} className="spin" aria-hidden />{t('st_rendering')}</span> : null}
      {node.render?.status === 'failed' ? <div className="alert alert-error small">{t('renderFailed')}</div> : null}
      {node.reason ? <p className="muted small" style={{ margin: 0 }}><strong>{t('why')}:</strong> {node.reason}</p> : null}
      {node.copy ? <p className="small" dir="auto" style={{ margin: 0, whiteSpace: 'pre-wrap' }}><strong>{t('postCopy')}:</strong> {node.copy}</p> : null}
      {error ? <ErrorBox error={error} /> : null}
      {rerender.error ? <ErrorBox error={rerender.error} /> : null}
      {allFormats.error ? <ErrorBox error={allFormats.error} /> : null}
      {allFormats.data ? (
        <div role="status" className="alert alert-info small">{allFormats.data.created.length ? t('formatsCreated', { n: allFormats.data.created.length }) : t('formatsExisting')}</div>
      ) : null}
      <div className="row wrap">
        <button type="button" className="btn btn-inv" disabled={!ready} onClick={() => dl('mp4')}><Download size={14} aria-hidden />{t('download')} MP4</button>
        <button type="button" className="btn" disabled={!ready || !r?.srt_url} onClick={() => dl('srt')}>{t('downloadSrt')}</button>
        {can(membership?.role, 'videos.edit') ? (
          <div className="row push">
            <Segmented label={t('format')} size="sm" dirLtr value={format} onChange={setFormat} options={RENDER_FORMATS.map((f) => ({ value: f, label: <span className="mono">{f}</span> }))} />
            <button type="button" className="btn btn-sm" disabled={rerender.isPending} onClick={() => rerender.mutate(undefined)}>{t('rerender')}</button>
            <button type="button" className="btn btn-sm" title={t('allFormatsTip')} disabled={allFormats.isPending || node.status === 'rejected'} onClick={() => allFormats.mutate(undefined)}><Copy size={12} aria-hidden />{t('allFormats')}</button>
            {sessionId ? <button type="button" className="btn btn-sm" aria-expanded={cutting} disabled={node.status === 'rejected'} onClick={() => setCutting((c) => !c)}><Scissors size={12} aria-hidden />{t('cutEdit')}</button> : null}
          </div>
        ) : null}
        <CreateTaskButton target={{ edit_id: node.id, label: node.title }} className="btn btn-sm" />
      </div>
      {cutting && sessionId ? <Suspense fallback={<Spinner />}><CutEditor node={node} sessionId={sessionId} /></Suspense> : null}
    </Modal>
  );
}

// ── Node pieces ──────────────────────────────────────────────────
/** UI status of a node: an AI or director draft is "Proposed" (amendment 1); a human draft stays "Draft". */
export function nodeStatus(n: DrillNode): 'proposed' | 'draft' | 'approved' | 'rejected' {
  if (n.status === 'approved' || n.status === 'rejected') return n.status;
  return n.origin === 'ai' || n.origin === 'director' ? 'proposed' : 'draft';
}
const renderBusy = (n: DrillNode) => !!n.render && (n.render.status === 'queued' || n.render.status === 'rendering');

function useDecide(episodeId: string) {
  const qc = useQueryClient();
  const w = useWs();
  return useWsMutation(
    async ({ node, decision }: { node: DrillNode; decision: 'approve' | 'reject' }) => {
      await (decision === 'approve' ? api.pipeline.approve(node.id, node.version) : api.pipeline.reject(node.id, undefined, node.version));
      logInfo('pipeline', `${decision} edit=${node.id} tier=${node.tier}`);
      return { node, decision };
    },
    {
      onSuccess: ({ node, decision }) => {
        // Optimistic local update while the drill-down refetches.
        qc.setQueryData<Drilldown>(qk.drilldown(w, episodeId), (d) => {
          if (!d) return d;
          const fix = (n: DrillNode) => (n.id === node.id ? { ...n, status: decision === 'approve' ? 'approved' : 'rejected' } as DrillNode : n);
          return { ...d, full: d.full ? fix(d.full) : null, value: d.value.map(fix), social: d.social.map(fix) };
        });
      },
    },
  );
}

function NodeButtons({ node, decide, onPlay, compact }: { node: DrillNode; decide: ReturnType<typeof useDecide>; onPlay: () => void; compact?: boolean }) {
  const { t } = useT();
  const membership = useMembership();
  const mayDecide = can(membership?.role, 'approve.internal') || can(membership?.role, 'videos.edit');
  const busy = decide.isPending && decide.variables?.node.id === node.id;
  const cls = compact ? 'btn btn-xs' : 'btn btn-sm';
  return (
    <div className="row wrap" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
      {mayDecide && node.status !== 'approved' ? (
        <button type="button" className={`${cls} btn-inv`} disabled={busy} onClick={() => decide.mutate({ node, decision: 'approve' })}>{t('approve')}</button>
      ) : null}
      {mayDecide && node.status !== 'rejected' ? (
        <button type="button" className={cls} disabled={busy} onClick={() => decide.mutate({ node, decision: 'reject' })}>{t('reject')}</button>
      ) : null}
      <button type="button" className={cls} onClick={onPlay} aria-label={`${t('play')} · ${node.title}`}><Play size={12} aria-hidden />{compact ? null : t('play')}</button>
    </div>
  );
}

/** Format badge on a sibling that shares another edit's cut ("All formats", amendment 11). */
function VariantBadge({ node }: { node: DrillNode }) {
  const { t } = useT();
  if (!node.variant_of) return null;
  return <span className="tag" title={t('variantBadge')}><Ltr className="mono">{node.format}</Ltr> · {t('variantBadge')}</span>;
}

function Thumb({ node, aspect }: { node: DrillNode; aspect: string }) {
  return (
    <div className="media stripes" style={{ aspectRatio: aspect, borderRadius: 4 }}>
      {node.render?.poster_url ? <img src={node.render.poster_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
      <span className="dur" style={{ bottom: 4, right: 4 }}>{clipDuration(node.duration_ms)}</span>
      {renderBusy(node) ? <LoaderCircle size={14} className="spin" aria-label="rendering" style={{ position: 'absolute', top: 6, left: 6, color: '#F6F4EF' }} /> : null}
      {node.render?.status === 'failed' ? <CircleAlert size={14} aria-label="render failed" style={{ position: 'absolute', top: 6, left: 6, color: '#FF6B54' }} /> : null}
    </div>
  );
}

function TierHead({ n, title, sub, side, accent }: { n: number; title: string; sub: string; side?: React.ReactNode; accent?: boolean }) {
  return (
    <div style={{ padding: '12px 16px', display: 'grid', gap: 2, borderBottom: '1px solid var(--line)' }}>
      <div className="row">
        <span className="mono" style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, background: accent ? 'var(--accent)' : 'var(--inv)', color: accent ? 'var(--on-accent)' : 'var(--on-inv)' }}>{n}</span>
        <h2 className="h2">{title}</h2>
        {side ? <span className="muted small push ellipsis" style={{ maxWidth: '55%' }}>{side}</span> : null}
      </div>
      <span className="muted small">{sub}</span>
    </div>
  );
}

function Slider({ label, min, max, step = 1, value, onChange, display }: { label: string; min: number; max: number; step?: number; value: number; onChange: (v: number) => void; display: string }) {
  return (
    <label style={{ display: 'grid', gridTemplateColumns: '96px minmax(0,1fr) 56px', gap: 10, alignItems: 'center', fontSize: 12 }}>
      <span className="muted">{label}</span>
      <input type="range" dir="ltr" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <Ltr className="mono" ><span style={{ display: 'block', textAlign: 'end' }}>{display}</span></Ltr>
    </label>
  );
}

// ── Tiers ────────────────────────────────────────────────────────
function useRegenerate(episodeId: string, tier: Tier) {
  const qc = useQueryClient();
  const w = useWs();
  return useWsMutation(
    async (body: Parameters<typeof api.pipeline.regenerate>[2]) => {
      logInfo('pipeline', `regenerate episode=${episodeId} tier=${tier} ${JSON.stringify(body)}`);
      return api.pipeline.regenerate(episodeId, tier, body);
    },
    {
      // 202: an analyze job was queued. Refresh the session (it turns busy) so the drill-down keeps polling.
      onSuccess: (job) => {
        logInfo('pipeline', `regenerate queued job=${job?.job_id ?? '?'} tier=${tier}`);
        void qc.invalidateQueries({ queryKey: ['ws', w, 'session'] });
      },
    },
  );
}

function DrilldownView({ episodeId, data, busy, presence, onOpen }: { episodeId: string; data: Drilldown; busy: boolean; presence: Presence | null; onOpen: (editId: string | null) => void }) {
  const { t, td } = useT();
  const membership = useMembership();
  const decide = useDecide(episodeId);
  const [playing, setPlaying] = useState<DrillNode | null>(null);
  // Presence (plan §19.4): an open video counts as "editing" it, so two people on one video are warned.
  useEffect(() => onOpen(playing?.id ?? null), [playing, onOpen]);
  const [selValue, setSelValue] = useState<string | null>(data.value[0]?.id ?? null);
  const [valLen, setValLen] = useState(150);
  const [valCount, setValCount] = useState(Math.min(8, Math.max(3, data.value.length || 5)));
  const [socLen, setSocLen] = useState(30);
  const [hook, setHook] = useState<(typeof HOOK_STYLES)[number]>('question');
  const [mood, setMood] = useState<(typeof SOCIAL_MOODS)[number]>('upbeat');
  const [pace, setPace] = useState<(typeof PACES)[number]>('punchy');
  const regenValue = useRegenerate(episodeId, 'value');
  const regenSocial = useRegenerate(episodeId, 'social');
  const mayGenerate = can(membership?.role, 'ai.generate');
  useEffect(() => {
    if (!selValue || !data.value.some((v) => v.id === selValue)) setSelValue(data.value[0]?.id ?? null);
  }, [data.value, selValue]);
  const selected = data.value.find((v) => v.id === selValue) ?? null;
  const socials = data.social.filter((s) => s.parent_edit_id === selValue);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, alignItems: 'start' }}>
      {/* 1 · Full episode */}
      <section className="card" aria-label={t('tFull')}>
        <TierHead n={1} title={t('tFull')} sub={t('tFullSub')} side={data.full ? <Ltr className="mono">{timecode(data.full.duration_ms)}</Ltr> : undefined} />
        {data.full ? (
          <div style={{ padding: '14px 16px', display: 'grid', gap: 12 }}>
            <div style={{ position: 'relative' }}>
              <Thumb node={data.full} aspect="16/9" />
              <span style={{ position: 'absolute', bottom: 8, left: 8 }}><StatusPill status={nodeStatus(data.full)} /></span>
            </div>
            {data.full.reason ? <span className="muted small">{data.full.reason}</span> : null}
            <NodeButtons node={data.full} decide={decide} onPlay={() => setPlaying(data.full)} />
          </div>
        ) : (
          <Empty>{t('noDrilldown')}</Empty>
        )}
      </section>

      {/* 2 · Value videos */}
      <section className="card" aria-label={t('tValue')}>
        <TierHead n={2} title={t('tValue')} sub={t('tValueSub')} side={t('videosCount', { n: data.value.length })} />
        {mayGenerate ? (
          <div style={{ padding: '12px 16px', display: 'grid', gap: 10, borderBottom: '1px solid var(--line)', background: 'var(--card-2)' }}>
            <Slider label={t('targetLen')} min={60} max={240} step={10} value={valLen} onChange={setValLen} display={mmss(valLen)} />
            <Slider label={t('howMany')} min={3} max={8} value={valCount} onChange={setValCount} display={String(valCount)} />
            <div className="row">
              <span className="muted small" title={t('regenKeeps')}>{t('regenKeeps')}</span>
              <button type="button" className="btn btn-sm push" disabled={regenValue.isPending || busy} onClick={() => regenValue.mutate({ target_len_s: valLen, count: valCount })}>{regenValue.isPending || busy ? t('regenRun') : t('regen')}</button>
            </div>
            {regenValue.error ? <ErrorBox error={regenValue.error} /> : null}
          </div>
        ) : null}
        <div role="listbox" aria-label={t('tValue')} style={{ padding: 10, display: 'grid', gap: 8, opacity: regenValue.isPending || (busy && regenValue.isSuccess) ? 0.35 : 1, transition: 'opacity 200ms' }}>
          {data.value.length === 0 ? <Empty>{t('noDrilldown')}</Empty> : null}
          {data.value.map((v) => {
            const nSoc = data.social.filter((s) => s.parent_edit_id === v.id).length;
            return (
              <div
                key={v.id}
                role="option"
                aria-selected={v.id === selValue}
                tabIndex={0}
                className="node"
                data-status={nodeStatus(v)}
                data-selected={v.id === selValue}
                onClick={() => setSelValue(v.id)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setSelValue(v.id))}
                style={{ display: 'grid', gridTemplateColumns: '104px minmax(0,1fr)', gap: 12, padding: 8, cursor: 'pointer' }}
              >
                <Thumb node={v} aspect="16/9" />
                <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, lineHeight: '18px' }}>{v.title}</span>
                  <div className="row wrap" style={{ gap: 6, fontSize: 11 }}>
                    {v.topic ? <span className="tag">{v.topic}</span> : null}
                    {v.score !== null ? <span className="mono" style={{ fontWeight: 500 }} title={t('score')}>{v.score.toFixed(1)}</span> : null}
                    <StatusPill status={nodeStatus(v)} />
                    <VariantBadge node={v} />
                    <span className="muted push">{t('socialCount', { n: nSoc })}</span>
                  </div>
                  <NodeButtons node={v} decide={decide} onPlay={() => setPlaying(v)} compact />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 3 · Social videos */}
      <section className="card" aria-label={t('tSocial')}>
        <TierHead n={3} title={t('tSocial')} sub={t('tSocialSub')} side={selected ? `← ${selected.title}` : undefined} accent />
        {mayGenerate && selected ? (
          <div style={{ padding: '12px 16px', display: 'grid', gap: 10, borderBottom: '1px solid var(--line)', background: 'var(--card-2)' }}>
            <Slider label={t('targetLen')} min={15} max={50} value={socLen} onChange={setSocLen} display={mmss(socLen)} />
            <div className="row wrap" style={{ gap: 6 }}><span className="muted small" style={{ width: 96 }}>{t('hookStyle')}</span><ChipGroup size="sm" label={t('hookStyle')} value={hook} onChange={setHook} options={HOOK_STYLES.map((h) => ({ value: h, label: td(`hook_${h}`) }))} /></div>
            <div className="row wrap" style={{ gap: 6 }}><span className="muted small" style={{ width: 96 }}>{t('music')}</span><ChipGroup size="sm" label={t('music')} value={mood} onChange={setMood} options={SOCIAL_MOODS.map((m) => ({ value: m, label: td(`smood_${m}`) }))} /></div>
            <div className="row wrap" style={{ gap: 6 }}>
              <span className="muted small" style={{ width: 96 }}>{t('pace')}</span>
              <ChipGroup size="sm" label={t('pace')} value={pace} onChange={setPace} options={PACES.map((p) => ({ value: p, label: td(`pace_${p}`) }))} />
              <button type="button" className="btn btn-sm push" disabled={regenSocial.isPending || busy} onClick={() => regenSocial.mutate({ target_len_s: socLen, count: 3, hook_style: hook, pace, music_mood: mood })}>{regenSocial.isPending || busy ? t('regenRun') : t('regen')}</button>
            </div>
            {regenSocial.error ? <ErrorBox error={regenSocial.error} /> : null}
          </div>
        ) : null}
        {!selected ? <Empty>{t('selectValue')}</Empty> : null}
        <div style={{ padding: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8, opacity: regenSocial.isPending || (busy && regenSocial.isSuccess) ? 0.35 : 1, transition: 'opacity 200ms' }}>
          {socials.map((s) => (
            <article key={s.id} className="node" data-status={nodeStatus(s)} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <button type="button" onClick={() => setPlaying(s)} aria-label={`${t('play')} · ${s.title}`} style={{ border: 'none', padding: 0, background: 'none' }}>
                <div className="media stripes" style={{ aspectRatio: '9/14', borderRadius: 0 }}>
                  {s.render?.poster_url ? <img src={s.render.poster_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                  <span className="dur" style={{ top: 6, right: 6 }}>{clipDuration(s.duration_ms)}</span>
                  {s.topic ? <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'var(--accent)', color: 'var(--on-accent)' }}>{s.topic.toUpperCase()}</span> : null}
                  <div style={{ position: 'absolute', insetInline: 8, top: '26%', fontFamily: 'Rubik', fontWeight: 800, fontSize: 12, lineHeight: '16px', color: '#121318', textAlign: 'start' }} dir="auto">
                    <span style={{ background: '#F6F4EF', padding: '1px 4px', borderRadius: 3, boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}>{s.title}</span>
                  </div>
                </div>
              </button>
              <div style={{ padding: 8, display: 'grid', gap: 6 }}>
                <div className="row" style={{ gap: 4, fontSize: 10 }}>
                  {s.score !== null ? <span className="mono" style={{ fontWeight: 500 }}>{s.score.toFixed(1)}</span> : null}
                  <StatusPill status={nodeStatus(s)} />
                  <VariantBadge node={s} />
                </div>
                <NodeButtons node={s} decide={decide} onPlay={() => setPlaying(s)} compact />
              </div>
            </article>
          ))}
        </div>
      </section>
      {/* 4 · Clips a person made from a marked range (§16 item 7) */}
      {data.custom?.length ? (
        <section className="card" aria-label={t('clipsTitle')} style={{ gridColumn: '1 / -1' }}>
          <TierHead n={4} title={t('clipsTitle')} sub={t('clipsSub')} side={t('videosCount', { n: data.custom.length })} />
          <div style={{ padding: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
            {data.custom.map((c) => (
              <div key={c.id} className="node" data-status={nodeStatus(c)} style={{ display: 'grid', gridTemplateColumns: '84px minmax(0,1fr)', gap: 10, padding: 8 }}>
                <Thumb node={c} aspect={c.format.replace(':', '/')} />
                <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                  <span className="ellipsis" style={{ fontSize: 13, fontWeight: 500 }}>{c.title}</span>
                  <div className="row" style={{ gap: 6, fontSize: 11 }}><StatusPill status={nodeStatus(c)} /><span className="mono muted">{c.format}</span></div>
                  <NodeButtons node={c} decide={decide} onPlay={() => setPlaying(c)} compact />
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {decide.error ? <div style={{ gridColumn: '1 / -1' }}><ErrorBox error={decide.error} /></div> : null}
      {playing ? <PlayerModal node={playing} sessionId={data.session?.id ?? null} presence={presence} onClose={() => setPlaying(null)} /> : null}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────
export function EpisodePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const ctx = useEpisodeContext();
  const membership = useMembership();
  const session = ctx.session.data;
  const busy = isSessionBusy(session);
  // Only an analyze job (initial plan or a regenerate) re-plans tiers; renders alone don't block regenerate.
  const analyzing = busy && !!session?.stages.some((s) => s.stage === 'analyze' && (s.status === 'running' || s.status === 'queued'));
  // The tree exists once a run has analysed the episode; during a regenerate the old nodes stay visible.
  const hasRun = !!session && session.status !== 'uploading';
  const drill = useDrilldown(ctx.episodeId, hasRun, busy);
  const qc = useQueryClient();
  const w = useWs();
  const [packState, setPackState] = useState<string | null>(null);
  const [packError, setPackError] = useState<unknown>(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [nleState, setNleState] = useState<string | null>(null);
  const [openEdit, setOpenEdit] = useState<string | null>(null);
  // Only people who can change videos count as editing; everyone else is "viewing".
  const presence = usePresence(ctx.episodeId, can(membership?.role, 'videos.edit') ? openEdit : null, can(membership?.role, 'tasks.view'));

  const nodes = useMemo(() => (drill.data ? [drill.data.full, ...drill.data.value, ...drill.data.social].filter((n): n is DrillNode => !!n) : []), [drill.data]);
  const titleOf = useCallback((editId: string) => [...nodes, ...(drill.data?.custom ?? [])].find((n) => n.id === editId)?.title ?? '', [nodes, drill.data]);
  const approved = nodes.filter((n) => n.status === 'approved');
  // A pack needs a ready render for every edit (the API answers 409 otherwise).
  const packable = approved.filter((n) => n.render?.status === 'ready');
  const topProposed = nodes.filter((n) => nodeStatus(n) === 'proposed' && (n.score ?? 0) >= APPROVE_ALL_SCORE);

  const approveAll = async () => {
    setApprovingAll(true);
    setPackError(null);
    const results = await Promise.allSettled(topProposed.map((node) => api.pipeline.approve(node.id, node.version)));
    const failed = results.filter((r) => r.status === 'rejected');
    logInfo('pipeline', `approve all ≥ ${APPROVE_ALL_SCORE}: ${results.length - failed.length}/${results.length} approved episode=${ctx.episodeId}`);
    if (failed.length) {
      logError('pipeline', `approve all: ${failed.length} approval(s) failed`, (failed[0] as PromiseRejectedResult).reason);
      setPackError((failed[0] as PromiseRejectedResult).reason);
    }
    await qc.invalidateQueries({ queryKey: qk.drilldown(w, ctx.episodeId) });
    setApprovingAll(false);
  };
  const pack = async () => {
    setPackError(null);
    setPackState('queued');
    try {
      await downloadPack(packable.map((n) => n.id), setPackState);
    } catch (e) {
      setPackError(e);
    } finally {
      setTimeout(() => setPackState(null), 1500);
    }
  };
  // Editor timelines need no finished render: they cut the original recordings (amendment 11).
  const nle = async () => {
    setPackError(null);
    setNleState('queued');
    try {
      await downloadPack(approved.map((n) => n.id), setNleState, { extras: TIMELINE_EXTRAS, includeVideo: false });
    } catch (e) {
      setPackError(e);
    } finally {
      setTimeout(() => setNleState(null), 1500);
    }
  };
  // Approvals (plan §17): approved videos go to internal review, then the customer.
  const sendToApproval = useWsMutation(() => api.approvals.request(approved.map((n) => n.id)), {
    onSuccess: (r) => {
      logInfo('approvals', `sent to approval created=${r.created.length} existing=${r.existing.length} episode=${ctx.episodeId}`);
      navigate(`/approvals/${r.items[0]?.edit_id ?? ''}`);
    },
  });
  const base = `/projects/${ctx.projectId}/episodes/${ctx.episodeId}`;
  const mayApprove = can(membership?.role, 'approve.internal') || can(membership?.role, 'videos.edit');

  return (
    <div className="page" style={{ maxWidth: 'none', gap: 18 }}>
      <QueryView query={ctx.project}>
        {(project) =>
          !ctx.episode ? (
            <div className="alert alert-error">{t('episodeNotFound')}</div>
          ) : (
            <>
              <EpisodeHeader
                project={project}
                episode={ctx.episode}
                actions={
                  nodes.length ? (
                    <>
                      <CreateTaskButton target={{ episode_id: ctx.episodeId, label: ctx.episode.title }} />
                      <Link to={`${base}/transcript`} className="btn"><FileText size={14} aria-hidden />{t('openTranscript')}</Link>
                      {mayApprove ? <button type="button" className="btn" disabled={approvingAll || topProposed.length === 0} onClick={approveAll}>{t('approveTop', { score: APPROVE_ALL_SCORE })}</button> : null}
                      {can(membership?.role, 'videos.edit') ? (
                        <button type="button" className="btn" title={t('sendToApprovalHint')} disabled={approved.length === 0 || sendToApproval.isPending} onClick={() => sendToApproval.mutate(undefined)}>
                          <CircleCheckBig size={14} aria-hidden />
                          {t('sendToApproval', { n: approved.length })}
                        </button>
                      ) : null}
                      <button type="button" className="btn btn-primary" title={t('packHint')} disabled={packable.length === 0 || !!packState} onClick={pack}>
                        <Download size={14} aria-hidden />
                        {packState === 'ready' ? t('packReady') : packState ? t('packPreparing') : t('downloadPack', { n: packable.length })}
                      </button>
                      <button type="button" className="btn" title={t('nleHint')} disabled={approved.length === 0 || !!nleState} onClick={nle}>
                        <Clapperboard size={14} aria-hidden />
                        {nleState === 'ready' ? t('packReady') : nleState ? t('nlePreparing') : t('nleDownload')}
                      </button>
                    </>
                  ) : null
                }
              />
              <PresenceBar presence={presence} videoTitle={titleOf} />
              {packError ? <ErrorBox error={packError} /> : null}
              {sendToApproval.error ? <ErrorBox error={sendToApproval.error} /> : null}
              {ctx.sessions.isLoading ? <Spinner label={t('loading')} /> : null}
              {ctx.sessions.error ? <ErrorBox error={ctx.sessions.error} onRetry={() => ctx.sessions.refetch()} /> : null}
              {ctx.sessionsResolved && !ctx.sessionId ? (
                <div className="card card-pad" style={{ padding: 24 }}>
                  <span>{t('noSession')}</span>
                  <Link to={`${base}/upload`} className="btn btn-primary" style={{ justifySelf: 'start' }}><Upload size={14} aria-hidden />{t('uploadMore')}</Link>
                </div>
              ) : null}
              {session ? <DeviceRouting session={session} episodePath={base} /> : null}
              {session ? <StageBar session={session} /> : null}
              {session && hasAnalysis(session) ? <FixEnhancePanel session={session} episodeId={ctx.episodeId} /> : null}
              {session && session.status === 'uploading' ? (
                <div className="card card-pad" style={{ padding: 20 }}>
                  <span>{t('needOneFile')}</span>
                  <Link to={`${base}/upload`} className="btn btn-primary" style={{ justifySelf: 'start' }}><Upload size={14} aria-hidden />{t('uploadMore')}</Link>
                </div>
              ) : null}
              {hasRun ? (
                <QueryView query={drill}>
                  {(d) => (nodes.length ? <DrilldownView episodeId={ctx.episodeId} data={d} busy={analyzing} presence={presence} onOpen={setOpenEdit} /> : <div className="card"><Empty>{t('noDrilldown')}</Empty></div>)}
                </QueryView>
              ) : null}
            </>
          )
        }
      </QueryView>
    </div>
  );
}
